import { AlertTriangle, LifeBuoy, LogIn, RefreshCw, ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { ReneoLogo } from '@/shared/components/ui/reneo-logo';

type BootstrapErrorVariant = 'config' | 'auth';

interface BootstrapErrorScreenProps {
    variant: BootstrapErrorVariant;
    onReload: () => void;
    onRelogin?: () => void | Promise<void>;
}

const errorScreenContent: Record<
    BootstrapErrorVariant,
    {
        titleKey: string;
        descriptionKey: string;
        icon: typeof AlertTriangle;
        accentClassName: string;
        suggestionKeys: string[];
    }
> = {
    config: {
        titleKey: 'bootstrapError.config.title',
        descriptionKey: 'bootstrapError.config.description',
        icon: AlertTriangle,
        accentClassName: 'bg-amber-50 text-amber-600 ring-amber-100',
        suggestionKeys: ['bootstrapError.config.suggestions.reload', 'bootstrapError.config.suggestions.support'],
    },
    auth: {
        titleKey: 'bootstrapError.auth.title',
        descriptionKey: 'bootstrapError.auth.description',
        icon: ShieldAlert,
        accentClassName: 'bg-blue-50 text-blue-700 ring-blue-100',
        suggestionKeys: ['bootstrapError.auth.suggestions.relogin', 'bootstrapError.auth.suggestions.reload'],
    },
};

export function BootstrapErrorScreen({ variant, onReload, onRelogin }: BootstrapErrorScreenProps) {
    const { t } = useTranslation('main');
    const { titleKey, descriptionKey, icon: Icon, accentClassName, suggestionKeys } = errorScreenContent[variant];

    return (
        <div className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top,_rgba(145,255,3,0.12),_transparent_28%),linear-gradient(180deg,_#f8fbfd_0%,_#eef4f7_100%)] px-6 py-10">
            <div className="w-full max-w-3xl space-y-6">
                <div className="flex flex-col items-center gap-3 text-center">
                    <div className="rounded-full border border-white/80 bg-white/90 px-5 py-3 shadow-sm">
                        <ReneoLogo />
                    </div>
                    <div className="space-y-1">
                        <p className="font-medium text-[#012A3C] text-xs uppercase tracking-[0.3em]">Reneo Asset Management</p>
                        <p className="text-muted-foreground text-sm">{t('bootstrapError.subtitle')}</p>
                    </div>
                </div>

                <Card className="overflow-hidden border-white/80 bg-white/95 shadow-xl backdrop-blur-sm">
                    <CardHeader className="border-b border-slate-200/80 bg-slate-50/70 pb-5">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                            <div className={`flex size-14 shrink-0 items-center justify-center rounded-2xl ring-4 ${accentClassName}`}>
                                <Icon className="size-6" />
                            </div>
                            <div className="space-y-2">
                                <CardTitle className="text-2xl text-[#012A3C]">{t(titleKey)}</CardTitle>
                                <CardDescription className="max-w-2xl text-[15px] leading-6">{t(descriptionKey)}</CardDescription>
                            </div>
                        </div>
                    </CardHeader>

                    <CardContent className="space-y-6 pt-6">
                        <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-5">
                            <p className="font-semibold text-[#012A3C] text-sm">{t('bootstrapError.actionListTitle')}</p>
                            <div className="mt-3 space-y-3">
                                {suggestionKeys.map(suggestionKey => (
                                    <div key={suggestionKey} className="flex items-start gap-3 text-muted-foreground text-sm leading-6">
                                        <span className="mt-2 size-2 shrink-0 rounded-full bg-[#91FF03]" />
                                        <span>{t(suggestionKey)}</span>
                                    </div>
                                ))}
                                <div className="flex items-start gap-3 text-muted-foreground text-sm leading-6">
                                    <span className="mt-2 size-2 shrink-0 rounded-full bg-[#91FF03]" />
                                    <span>
                                        {t('bootstrapError.supportEmailPrefix')}{' '}
                                        <a className="font-medium text-[#012A3C] underline underline-offset-2" href="mailto:support@reneo.de">
                                            support@reneo.de
                                        </a>{' '}
                                        {t('bootstrapError.supportEmailSuffix')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </CardContent>

                    <CardFooter className="flex flex-col gap-3 border-t border-slate-200/80 bg-slate-50/60 pt-6 sm:flex-row sm:justify-between">
                        <div className="flex items-center gap-2 text-muted-foreground text-sm">
                            <LifeBuoy className="size-4 text-[#012A3C]" />
                            <span>{t('bootstrapError.supportAvailable')}</span>
                        </div>
                        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                            {variant === 'auth' && onRelogin ? (
                                <Button className="w-full sm:w-auto" onClick={() => void onRelogin()} variant="secondary">
                                    <LogIn className="size-4" />
                                    {t('bootstrapError.signInAgain')}
                                </Button>
                            ) : null}
                            <Button className="w-full sm:w-auto" onClick={onReload} variant="primary">
                                <RefreshCw className="size-4" />
                                {t('bootstrapError.reloadPage')}
                            </Button>
                        </div>
                    </CardFooter>
                </Card>
            </div>
        </div>
    );
}
